/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { api, ApiError } from "core/api";
import { Button, SvgIcon, Card, Input, Alert, ControlledTable, Select, Switch } from "core/components";
import { ChangeOrderStatus } from "core/schema";
import { approve_admin_change_order_path, reject_admin_change_order_path, admin_change_orders_path, admin_change_order_path } from "routes";
import moment from "moment";
import formatter from "core/formatter";
import ChangeOrderItems from "./ChangeOrderItems";
import ControlledTableContext from "core/components/ControlledTableContext";
import { Controller, useForm } from "react-hook-form";

const ChangeOrders = ({ has_change_orders, statuses }): JSX.Element => {
  const {
    control,
    formState: { isSubmitting, isValidating },
    handleSubmit,
    reset
  } = useForm({
    mode: "onChange"
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [changeOrders, setChangeOrders] = React.useState([]);
  const [changeOrder, setChangeOrder] = React.useState(null);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [showChangeOrderModal, setShowChangeOrderModal] = React.useState(false);
  const [showApproveConfirmationModal, setShowApproveConfirmationModal] = React.useState(false);
  const [showRejectConfirmationModal, setShowRejectConfirmationModal] = React.useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "code",
        Header: "Change Order ID",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "project_code",
        Header: "Project ID",
        accessor: "project_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Budgeted Amount",
        accessor: "total_budgeted_amount",
        Cell: (props) => {
          const totalBudgetedAmount = formatter.format(props.value);

          return (
            <div title={totalBudgetedAmount} data-bs-toggle="tooltip">
              <p className="text-right text-truncate">{totalBudgetedAmount}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested By",
        accessor: "requested_by",
        Cell: (props) => {
          const requestedBy = props.value;

          return (
            <div title={requestedBy} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{requestedBy}</p>
            </div>
          );
        }
      },
      {
        Header: "Approved By",
        accessor: "approved_by",
        Cell: (props) => {
          const approvedBy = props.value || "-";

          return (
            <div title={approvedBy} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{approvedBy}</p>
            </div>
          );
        }
      },
      {
        id: "status",
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.change_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Updated",
        accessor: "updated_at",
        Cell: (props) => {
          const updatedAt = props.value ? moment(props.value).format("MM/DD/YYYY hh:mm a") : "-";
          return (
            <div title={updatedAt} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{updatedAt}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const { policy, status, has_transactions } = props.row.original;
          const document = props.row.original.documents_attributes[0];
          const isForApproval = status == ChangeOrderStatus.ForApproval;
          const isActive = status == ChangeOrderStatus.Active;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Change Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setChangeOrder(props.row.original);
                  setShowChangeOrderModal(!showChangeOrderModal);
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {(policy.approve || policy.reject) && isForApproval && (
                <>
                  <a
                    className="button button--action button--action-approve"
                    title="Approve Change Order"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      setChangeOrder(props.row.original);
                      setShowApproveConfirmationModal(!showApproveConfirmationModal);
                    }}
                  >
                    <SvgIcon name="check" className="h5" />
                  </a>
                  <a
                    className="button button--action button--action-danger"
                    title="Reject Change Order"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      setChangeOrder(props.row.original);
                      setShowRejectConfirmationModal(!showRejectConfirmationModal);
                    }}
                  >
                    <SvgIcon name="times" className="h5" />
                  </a>
                </>
              )}
              {(document?.file_url || document?.file_name) && (
                <>
                  <a
                    className="a button button--action button--action-info"
                    download={true}
                    target="_blank"
                    rel="noopener"
                    href={document?.file_url || document?.file_name}
                  >
                    <SvgIcon name="download" />
                  </a>
                </>
              )}
              {policy.destroy && (isForApproval || isActive) && !has_transactions && (
                <a
                  className="button button--action button--action-danger"
                  title="Delete Change Order"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setChangeOrder(props.row.original);
                    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
                  }}
                >
                  <SvgIcon name="delete" className="h6" />
                </a>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const getChangeOrders = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(admin_change_orders_path({ page, per, filters: JSON.stringify(filters) }), { signal });
      setChangeOrders(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No change orders.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Change Order ID" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Project ID" onChange={(e) => onChange("project_code", e.target.value)} />
        </Col>
        <Col>
          <Select
            placeholder="Filter by Status"
            options={statuses}
            isSearchable={false}
            getOptionLabel={(option) => I18n.t(`activerecord.attributes.change_order.statuses.${option.value}`)}
            getOptionValue={(option) => option.value}
            onChange={(newOption) => {
              if (newOption) {
                setStatusFilter(newOption.value);
                table.current.setFilter("status", newOption.value);
              } else {
                setStatusFilter("");
                table.current.setFilter("status", "");
              }
            }}
            value={statuses.find((status) => status.value == statusFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={changeOrders}
              ref={table}
              isLoading={isLoading}
              fetchData={getChangeOrders}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_change_orders ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  const save = async (data) => {
    setFormErrors([]);

    try {
      await api.put(admin_change_order_path(data.id), data);
      getChangeOrders({ page: 1, per: pagination?.limit || 10, filters: table.current.state.filters }).catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowChangeOrderModal(!showChangeOrderModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  const approve = async (id): Promise<unknown> => {
    setFormErrors([]);
    setStatusFilter("");
    try {
      await api.put(approve_admin_change_order_path(id));
      getChangeOrders({ page: 1, per: pagination?.limit || 10, filters: table.current.state.filters }).catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowApproveConfirmationModal(!showApproveConfirmationModal);
    }
  };

  const reject = async (id): Promise<unknown> => {
    setFormErrors([]);
    setStatusFilter("");
    try {
      await api.put(reject_admin_change_order_path(id));
      getChangeOrders({ page: 1, per: pagination?.limit || 10, filters: table.current.state.filters }).catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowRejectConfirmationModal(!showRejectConfirmationModal);
    }
  };

  const deleteChangeOrder = React.useCallback(async (id) => {
    setFormErrors([]);
    setStatusFilter("");
    try {
      const response = await api.deleteItem(admin_change_order_path(id));
      setChangeOrders((changeOrders) => changeOrders.filter((changeOrder) => changeOrder.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowDeleteConfirmationModal((value) => !value);
    }
  }, []);

  const canUpdate = (changeOrder) => {
    return [ChangeOrderStatus.ForApproval, ChangeOrderStatus.Active].includes(changeOrder?.status) && changeOrder?.policy?.update;
  };

  React.useEffect(() => {
    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const status = urlParams.get("status");

    if (status && status === "for_approval") {
      setStatusFilter(status);
      table.current.setFilter("status", status);
    }
  }, []);

  return (
    <React.StrictMode>
      <Card>
        <Card.Header title="Change Orders"></Card.Header>
        <Card.Body>
          <Container fluid={true}>
            <Row>
              <Col lg={6} className="d-flex">
                {formErrors && formErrors.length != 0 && (
                  <Alert>
                    {formErrors.map((error, index) => {
                      return <div key={index}>{error}</div>;
                    })}
                  </Alert>
                )}
              </Col>
            </Row>
            {content()}
          </Container>
        </Card.Body>
      </Card>
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showChangeOrderModal}>
        <Modal.Header style={{ display: "block" }}>
          {canUpdate(changeOrder) && (
            <Controller
              name="include_budget"
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  label="Include Budget"
                  info="Include or exclude change order to the budget amounts in the Actual vs Budget Report and Performance Evaluation Report"
                  checked={field.value}
                  value={field.value?.toString()}
                />
              )}
            />
          )}
          <p>{`Change Order: ${changeOrder?.code}`}</p>
          <div title={changeOrder?.description} data-bs-toggle="tooltip">
            <p className="text-truncate">{`Description: ${changeOrder?.description}`}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <ChangeOrderItems changeOrder={changeOrder} reset={reset} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowChangeOrderModal(!showChangeOrderModal);
            }}
            icon="times"
            title="Close"
          />
          {canUpdate(changeOrder) && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApproveConfirmationModal}>
        <Modal.Header>{`Approve Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApproveConfirmationModal(!showApproveConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve change order"
            onClick={() => {
              approve(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectConfirmationModal}>
        <Modal.Header>{`Reject Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectConfirmationModal(!showRejectConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="save"
            title="Yes, reject change order"
            onClick={() => {
              reject(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteConfirmationModal}>
        <Modal.Header>{`Delete Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete change order."
            onClick={() => {
              deleteChangeOrder(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ChangeOrders;
